import { useState, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';

import { useWorkspace } from '@/context';
import { Card } from '@/components/ui';
import { useOnboardingProgress } from '@/hooks/useOnboardingProgress';
import { useSavedActions } from '@/hooks/useSavedActions';
import { useLocalStorage } from '@/hooks/useLocalStorage';
import { useConversations } from '@/hooks/useConversations';
import { useStartFlow } from '@/hooks/useStartFlow';
import { STORAGE_KEYS } from '@/utils/constants';
import { conversationPath } from '@/config/routes';
import { resolveUserIntent } from '@/intent/resolveUserIntent';
import { storeConversationAttachment } from '@/utils/conversationAttachment';
import type { UserDraft } from '@/types/userCreation';
import { suggestEmailFromName } from '@/utils/provisionedUserEmail';
import { cn } from '@/utils/helpers';

import {
  GreetingHeader,
  QuickActionCards,
  ChatInput,
  SavedActions,
  RecentConversations,
  GetStartedBanner,
} from '@/components/Workspace';

import type { ChatTab, RecentConversationEntry } from '@/types';

function WorkspacePage() {
  const { effectiveRole, effectiveUser } = useWorkspace();
  const navigate = useNavigate();

  const { actions, saveAction, removeAction, togglePin } = useSavedActions(effectiveRole);
  const { completedCount, isStepComplete } = useOnboardingProgress();
  const { conversations, isLoading, error, fetchConversations, createConversation } =
    useConversations(effectiveRole);
  const { start: startDeviceSetup, error: deviceSetupStartError } = useStartFlow('device-setup');
  const { start: startUserCreation, error: userCreationStartError } = useStartFlow('user-creation');
  const { start: startAccessGroupCreation, error: accessGroupStartError } =
    useStartFlow('access-group-creation');
  const { start: startBulkUserImport, error: bulkUserImportStartError } =
    useStartFlow('bulk-user-import');

  const [onboardingDismissed, setOnboardingDismissed] = useLocalStorage(
    STORAGE_KEYS.ONBOARDING_DISMISSED,
    false
  );
  const [draftInjectKey, setDraftInjectKey] = useState(0);
  const [draftText, setDraftText] = useState('');
  const [activeChatTab, setActiveChatTab] = useState<ChatTab>('users');

  const isOperator = effectiveRole === 'operator';

  const injectDraft = useCallback((text: string) => {
    setDraftText(text);
    setDraftInjectKey((k) => k + 1);
  }, []);

  const recentConversations = useMemo<RecentConversationEntry[]>(
    () =>
      conversations.slice(0, 8).map((c) => ({
        id: c.id,
        title: c.title?.trim() ? c.title : 'Conversation',
        timestamp: c.updatedAt || c.createdAt || new Date().toISOString(),
      })),
    [conversations]
  );

  const handleSend = useCallback(
    async (msg: string, options?: { attachment?: File | null }) => {
      try {
        const resolved = resolveUserIntent(msg, options);
        if (!resolved) return;

        if (resolved.kind === 'flow') {
          const flowStarters = {
            'bulk-user-import': startBulkUserImport,
            'user-creation': startUserCreation,
            'access-group-creation': startAccessGroupCreation,
            'device-setup': startDeviceSetup,
          } as const;

          const start = flowStarters[resolved.flowId];
          let prefill = resolved.prefill;

          if (resolved.flowId === 'user-creation' && prefill && 'firstName' in prefill) {
            const userPrefill = prefill as Partial<UserDraft>;
            prefill = {
              ...userPrefill,
              email:
                userPrefill.email?.trim() ||
                suggestEmailFromName(
                  userPrefill.firstName ?? '',
                  userPrefill.lastName ?? '',
                  effectiveUser.email
                ),
            };
          }

          await start(resolved.message, {
            prefill: prefill && Object.keys(prefill).length > 0 ? prefill : undefined,
            attachment: options?.attachment,
          });
          return;
        }

        const conv = await createConversation({});
        if (options?.attachment) {
          await storeConversationAttachment(conv.id, options.attachment);
        }
        navigate(`${conversationPath(conv.id, effectiveRole)}?pendingSend=${encodeURIComponent(resolved.message)}`);
      } catch (e) {
        console.error(e);
      }
    },
    [
      createConversation,
      effectiveRole,
      effectiveUser.email,
      navigate,
      startAccessGroupCreation,
      startBulkUserImport,
      startDeviceSetup,
      startUserCreation,
    ]
  );

  const showGetStarted = !isOperator && !onboardingDismissed;

  return (
    <div className={cn('workspace', isOperator ? 'workspace--operator' : 'workspace--novice')}>
      <Card className="workspace__panel">
        <div className="workspace__content">
          <GreetingHeader user={effectiveUser} />

          {deviceSetupStartError || userCreationStartError || accessGroupStartError || bulkUserImportStartError ? (
            <p className="workspace__flow-error" role="alert">
              {deviceSetupStartError || userCreationStartError || accessGroupStartError || bulkUserImportStartError}
            </p>
          ) : null}

          {showGetStarted ? (
            <GetStartedBanner
              completedCount={completedCount}
              isStepComplete={isStepComplete}
              onDismiss={() => setOnboardingDismissed(true)}
              onTryStep={(stepId) => {
                if (stepId === 'device') {
                  void startDeviceSetup('Add a new controller for this site');
                }
                if (stepId === 'user') {
                  void startUserCreation('Create a new user with a card for this site');
                }
                if (stepId === 'access') {
                  void startAccessGroupCreation(
                    'Create a first-floor access group for lab reader 1, entrance reader 1, and server room'
                  );
                }
              }}
            />
          ) : (
            <QuickActionCards
              role={effectiveRole}
              category={activeChatTab}
              onSelect={(text) => {
                if (/controller/i.test(text)) {
                  void startDeviceSetup(text);
                } else if (/access\s*group/i.test(text)) {
                  void startAccessGroupCreation(text);
                } else if (/user|cardholder/i.test(text)) {
                  void startUserCreation(text);
                } else {
                  injectDraft(text);
                }
              }}
            />
          )}

          <ChatInput
            variant="workspace"
            showTabs
            userSite={effectiveUser.site}
            activeTab={activeChatTab}
            onActiveTabChange={setActiveChatTab}
            onSend={handleSend}
            savedActions={actions}
            draftInjectKey={draftInjectKey}
            draftText={draftText}
          />

          <div className="workspace-bottom">
            <SavedActions
              actions={actions}
              onSave={saveAction}
              onRemove={removeAction}
              onTogglePin={togglePin}
              onApplyTemplate={injectDraft}
            />
            <RecentConversations
              entries={recentConversations}
              onSelect={(id) => navigate(conversationPath(id, effectiveRole))}
              isLoading={isLoading}
              error={error}
              onRetry={fetchConversations}
            />
          </div>
        </div>
      </Card>
    </div>
  );
}

export default WorkspacePage;
